import { useState, useEffect, useMemo } from "react";
import MemberLayout from "@/layouts/MemberLayout";
import { 
  BarChart as BarChartIcon, Download, TrendingUp, DollarSign, 
  Activity, Calendar, PieChart as PieChartIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";
import { useToast } from "@/hooks/use-toast";
import { useGetMemberAppointments, useListCostEstimates } from "@/lib/api-client-react";
import { format } from "date-fns";
import { ListPagination } from "@/components/ui/list-pagination";


const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const DUMMY_REPORTS_APPOINTMENTS = [
  {
    id: "apt-rep-001",
    amount: 680,
    status: "COMPLETED",
    createdAt: new Date(2026, 2, 12, 10, 30).toISOString(),
    notes: "[ESTIMATE:Comprehensive Cardio Evaluation] Routine quarterly follow-up and ECG screening.",
    bundle: { name: "Comprehensive Cardio & Wellness Package", category: "Cardiology" },
    provider: {
      user: { displayName: "Dr. Sarah Mitchell, MD", firstName: "Sarah", lastName: "Mitchell" },
      practiceType: "Individual"
    }
  },
  {
    id: "apt-rep-002",
    amount: 1150,
    status: "COMPLETED",
    createdAt: new Date(2026, 1, 20, 14, 15).toISOString(),
    notes: "[ESTIMATE:Diagnostic Lumbar MRI] Imaging scan performed without contrast.",
    bundle: { name: "Advanced MRI Diagnostic Package", category: "Radiology & Imaging" },
    provider: {
      user: { displayName: "Dr. David Rodriguez, MD", firstName: "David", lastName: "Rodriguez" },
      practiceType: "Individual"
    }
  },
  {
    id: "apt-rep-003",
    amount: 440,
    status: "COMPLETED",
    createdAt: new Date(2026, 0, 18, 9, 0).toISOString(),
    notes: "Annual executive preventative wellness exam and full lipid panel.",
    bundle: { name: "Executive Health & Preventative Screening", category: "Preventative Care" },
    provider: {
      user: { displayName: "Dr. Emily Chen, MD", firstName: "Emily", lastName: "Chen" },
      practiceType: "Clinic"
    },
    groupName: "Horizon Primary Care Clinic"
  },
  {
    id: "apt-rep-004",
    amount: 960,
    status: "COMPLETED",
    createdAt: new Date(2026, 3, 5, 11, 45).toISOString(),
    notes: "Bilateral knee joint evaluation and guided cortisone injection.",
    bundle: { name: "Orthopedic Knee Joint & Rehab Package", category: "Orthopedics" },
    provider: {
      user: { displayName: "Dr. Robert Vance, MD", firstName: "Robert", lastName: "Vance" },
      practiceType: "Individual"
    }
  },
  {
    id: "apt-rep-005",
    amount: 480,
    status: "COMPLETED",
    createdAt: new Date(2026, 2, 28, 15, 30).toISOString(),
    notes: "Periodontal deep scaling and ultrasonic cleaning.",
    bundle: { name: "Complete Dental Restoration & Deep Cleaning", category: "Dental Care" },
    provider: {
      user: { displayName: "Dr. Marcus Thorne, DDS", firstName: "Marcus", lastName: "Thorne" },
      practiceType: "Individual"
    }
  },
  {
    id: "apt-rep-006",
    amount: 320,
    status: "APPROVED",
    createdAt: new Date(2026, 4, 10, 10, 0).toISOString(),
    notes: "Comprehensive dilated eye examination and retinal imaging.",
    bundle: { name: "Complete Vision Care & Optical Examination", category: "Ophthalmology" },
    provider: {
      user: { displayName: "Dr. Alena Patel, OD", firstName: "Alena", lastName: "Patel" },
      practiceType: "Individual"
    }
  },
  {
    id: "apt-rep-007",
    amount: 550,
    status: "PENDING",
    createdAt: new Date(2026, 4, 15, 13, 0).toISOString(),
    notes: "Physiotherapy follow-up evaluation and home exercise plan.",
    bundle: { name: "Spine & Posture Physical Therapy Package", category: "Physical Therapy" },
    provider: {
      user: { displayName: "Summit Physical Therapy", firstName: "Summit", lastName: "PT" },
      practiceType: "Group"
    },
    groupName: "Summit Physical Therapy Associates"
  }
];

const DUMMY_REPORTS_ESTIMATES = [
  {
    id: "est-rep-001",
    estimatedCost: 850,
    createdAt: new Date(2026, 0, 15).toISOString(),
  },
  {
    id: "est-rep-002",
    estimatedCost: 1550,
    createdAt: new Date(2026, 1, 10).toISOString(),
  },
  {
    id: "est-rep-003",
    estimatedCost: 1800,
    createdAt: new Date(2026, 2, 8).toISOString(),
  },
  {
    id: "est-rep-004",
    estimatedCost: 1350,
    createdAt: new Date(2026, 3, 2).toISOString(),
  },
  {
    id: "est-rep-005",
    estimatedCost: 950,
    createdAt: new Date(2026, 4, 1).toISOString(),
  }
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'PENDING': return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800/60';
    case 'APPROVED': return 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800/60';
    case 'REJECTED': return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-950/60 dark:text-red-300 dark:border-red-800/60';
    case 'COMPLETED': return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800/60';
    default: return 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
  }
};

export default function MemberReports() {
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [timeRange, setTimeRange] = useState("all");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try { setUser(JSON.parse(u)); } catch(e) {}
    }
  }, []);

  // Fetch real data
  const { data: apptsData } = useGetMemberAppointments({ limit: 1000 }, { query: { enabled: !!user } });
  const { data: estData } = useListCostEstimates({ limit: 1000 }, { query: { enabled: !!user } });
  
  const rawAppointments = apptsData?.items || [];
  const rawEstimates = estData?.items || [];

  const appointments = useMemo(() => {
    if (rawAppointments.length > 0 && rawAppointments.some((a: any) => a.status === 'COMPLETED')) {
      return rawAppointments;
    }
    if (rawAppointments.length > 0) {
      return [...rawAppointments, ...DUMMY_REPORTS_APPOINTMENTS];
    }
    return DUMMY_REPORTS_APPOINTMENTS;
  }, [rawAppointments]);

  const estimates = useMemo(() => {
    if (rawEstimates.length > 0) return rawEstimates;
    return DUMMY_REPORTS_ESTIMATES;
  }, [rawEstimates]);
  
  // Only count COMPLETED for totals and charts
  const completedAppointments = useMemo(() => {
    return appointments.filter((apt: any) => apt.status === 'COMPLETED');
  }, [appointments]);

  // Spending Over Time Data
  const spendingData = useMemo(() => {
    if (!completedAppointments.length && !estimates.length) {
      return ['Jan', 'Feb', 'Mar'].map(m => ({ name: m, outOfPocket: 0, estimatedCost: 0 }));
    }
    
    const monthMap: Record<string, { outOfPocket: number, estimatedCost: number }> = {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Add real appointments to out of pocket
    completedAppointments.forEach((apt: any) => {
      const date = new Date(apt.createdAt);
      if (isNaN(date.getTime())) return;
      const month = months[date.getMonth()];
      if (!monthMap[month]) monthMap[month] = { outOfPocket: 0, estimatedCost: 0 };
      monthMap[month].outOfPocket += Number(apt.amount || 0);
    });
    
    // Add estimates to "estimated Cost"
    estimates.forEach((est: any) => {
      const date = new Date(est.createdAt);
      if (isNaN(date.getTime())) return;
      const month = months[date.getMonth()];
      if (!monthMap[month]) monthMap[month] = { outOfPocket: 0, estimatedCost: 0 };
      monthMap[month].estimatedCost += Number(est.estimatedCost || 0);
    });

    return Object.keys(monthMap).sort((a, b) => months.indexOf(a) - months.indexOf(b)).map(m => ({
      name: m,
      ...monthMap[m]
    }));
  }, [completedAppointments, estimates]);

  // Provider-wise Spending
  const providerCostData = useMemo(() => {
    const pMap: Record<string, number> = {};
    completedAppointments.forEach((apt: any) => {
      let pName = apt.provider?.user?.displayName || apt.provider?.user?.firstName ? `${apt.provider.user.firstName} ${apt.provider.user.lastName}` : 'Unknown Provider';
      // If booked via a group, the primary entity the member spent money with is the group
      if (apt.groupName) {
        pName = apt.groupName;
      }
      pMap[pName] = (pMap[pName] || 0) + Number(apt.amount || 0);
    });
    return Object.entries(pMap).map(([name, cost]) => ({ name, cost })).sort((a, b) => b.cost - a.cost);
  }, [completedAppointments]);

  // Spending by Category
  const categoryData = useMemo(() => {
    const cMap: Record<string, number> = {};
    completedAppointments.forEach((apt: any) => {
      const cName = apt.bundle?.name || (apt.notes?.startsWith('[ESTIMATE:') ? apt.notes.match(/\[ESTIMATE:([^:\]]+)/)?.[1] : 'General Visit');
      cMap[cName] = (cMap[cName] || 0) + Number(apt.amount || 0);
    });
    return Object.entries(cMap).map(([name, value]) => ({ name, value }));
  }, [completedAppointments]);

  // Paginated appointments for the table
  const totalPages = Math.ceil(appointments.length / itemsPerPage);
  const totalCount = appointments.length;
  
  const recentAppointments = [...appointments]
    .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice((page - 1) * itemsPerPage, page * itemsPerPage)
    .map((apt: any) => {
      let individualName = apt.provider?.user?.displayName || apt.provider?.user?.firstName ? `${apt.provider.user.firstName} ${apt.provider.user.lastName}` : '';
      
      const isGroup = apt.groupName || 
                      apt.provider?.practiceType?.toLowerCase() === 'group' || 
                      apt.provider?.practiceType?.toLowerCase() === 'clinic';
      const pType = isGroup ? 'Group' : 'Individual';
      
      let mainProviderName = apt.groupName ? apt.groupName : (individualName || 'Unknown Provider');
      let assignedText = apt.groupName && individualName && individualName !== apt.groupName ? individualName : null;

      return {
        id: apt.id,
        date: format(new Date(apt.createdAt), 'MMM d, yyyy'),
        provider: mainProviderName,
        assignedTo: assignedText,
        type: pType,
        service: apt.bundle?.name || (apt.notes?.startsWith('[ESTIMATE:') ? apt.notes.match(/\[ESTIMATE:([^:\]]+)/)?.[1] : 'Consultation'),
        cost: Number(apt.amount || 0).toFixed(2),
        status: apt.status
      };
    });

  const handleExportCSV = () => {
    let csv = "Month,Out of Pocket Cost,Estimated Cost\\n";
    spendingData.forEach((row: any) => {
      csv += `${row.name},${row.outOfPocket},${row.estimatedCost}\\n`;
    });

    csv += "\\nAppointment History\\n";
    csv += "Date,Provider,Service,Cost,Status\\n";
    
    const exportAppointments = [...appointments].sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    exportAppointments.forEach((apt: any) => {
      const date = format(new Date(apt.createdAt), 'MMM d, yyyy');
      const provider = apt.provider?.user?.displayName || apt.groupName || 'Unknown Provider';
      const service = apt.bundle?.name || (apt.notes?.startsWith('[ESTIMATE:') ? apt.notes.match(/\[ESTIMATE:([^:\]]+)/)?.[1] : 'Consultation');
      const cost = Number(apt.amount || 0).toFixed(2);
      csv += `"${date}","${provider}","${service}",${cost},${apt.status}\\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `healthcare_spending_report_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({ title: "Export Successful", description: "Your spending report has been downloaded." });
  };

  const totalYtdSpend = spendingData.reduce((sum: number, row: any) => sum + row.outOfPocket, 0);
  const totalYtdEstimated = spendingData.reduce((sum: number, row: any) => sum + row.estimatedCost, 0);
  const totalSavings = totalYtdEstimated - totalYtdSpend;
  const displaySavings = totalSavings > 0 ? totalSavings : 0;

  return (
    <MemberLayout>
      <div className="w-full space-y-6 sm:space-y-7 pt-4 sm:pt-6 pb-16 flex flex-col min-h-[calc(100vh-10rem)]">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border/60">
          <div className="flex items-center gap-3.5 sm:gap-4 min-w-0">
            <div className="p-3 sm:p-3.5 bg-primary/10 text-primary rounded-2xl shrink-0 border border-primary/20 shadow-xs">
              <PieChartIcon className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
            </div>
            <div className="space-y-0.5 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground truncate">
                Healthcare Reports
              </h1>
              <p className="text-muted-foreground text-xs sm:text-sm font-medium">
                Track your real medical spending, savings, and care history.
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2.5 sm:gap-3 w-full sm:w-auto shrink-0">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="h-10 flex-1 sm:w-40 sm:flex-initial rounded-xl bg-background border-border text-xs sm:text-sm font-semibold cursor-pointer">
                <SelectValue placeholder="Select timeframe" />
              </SelectTrigger>
              <SelectContent className="rounded-xl z-50">
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>

            <Button
              onClick={handleExportCSV}
              className="h-10 px-4 flex-1 sm:flex-initial rounded-xl gap-2 shadow-xs bg-gradient-to-r from-primary to-secondary text-white font-semibold text-xs sm:text-sm cursor-pointer whitespace-nowrap border-0"
            >
              <Download className="w-4 h-4 shrink-0" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Top KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
          <div className="bg-card rounded-2xl p-4 sm:p-5 border border-border shadow-xs flex items-center gap-3.5 sm:gap-4 min-w-0 relative overflow-hidden transition-all hover:border-primary/30">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 flex items-center justify-center shrink-0">
              <DollarSign className="w-6 h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider truncate">
                Total Spending
              </p>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-foreground tracking-tight mt-0.5 truncate">
                ${totalYtdSpend.toLocaleString()}
              </h3>
            </div>
          </div>

          <div className="bg-card rounded-2xl p-4 sm:p-5 border border-border shadow-xs flex items-center gap-3.5 sm:gap-4 min-w-0 relative overflow-hidden transition-all hover:border-emerald-500/30">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 flex items-center justify-center shrink-0">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-1 flex-wrap">
                <p className="text-[11px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider truncate">
                  Total Savings vs Estimates
                </p>
                {displaySavings > 0 && (
                  <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20 shrink-0">
                    Target Beat
                  </span>
                )}
              </div>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 tracking-tight mt-0.5 truncate">
                ${displaySavings.toLocaleString()}
              </h3>
            </div>
          </div>

          <div className="bg-card rounded-2xl p-4 sm:p-5 border border-border shadow-xs flex items-center gap-3.5 sm:gap-4 min-w-0 relative overflow-hidden transition-all hover:border-purple-500/30">
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20 flex items-center justify-center shrink-0">
              <Calendar className="w-6 h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider truncate">
                Provider Visits
              </p>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-foreground tracking-tight mt-0.5 truncate">
                {completedAppointments.length}
              </h3>
            </div>
          </div>
        </div>

        {/* Spending Over Time (Primary Financial Trend) */}
        <div className="bg-card rounded-2xl sm:rounded-3xl border border-border shadow-xs p-4 sm:p-5 lg:p-6 flex flex-col">
          <div className="flex items-center justify-between pb-3 sm:pb-4 border-b border-border/60 mb-3 sm:mb-4">
            <h3 className="text-sm sm:text-base font-bold flex items-center gap-2 text-foreground">
              <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-primary"/> Spending Over Time
            </h3>
          </div>
          <div className="w-full h-[280px] sm:h-[320px]">
            {spendingData.length > 0 && (spendingData.some((d:any) => d.outOfPocket > 0 || d.estimatedCost > 0)) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={spendingData} margin={{ top: 12, right: 15, left: -10, bottom: 8 }}>
                  <defs>
                    <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0}/>
                    </linearGradient>
                    <linearGradient id="colorEst" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.25}/>
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} dy={5} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} width={48} tickFormatter={(v) => `$${v}`} />
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#94a3b8" opacity={0.15} />
                  <Tooltip 
                    formatter={(val: number, name: string) => [`$${val.toLocaleString()}`, name === 'outOfPocket' ? 'Actual Cost' : 'Estimated Cost']}
                    contentStyle={{ borderRadius: '12px', border: '1px solid rgba(148, 163, 184, 0.2)', backgroundColor: '#0f172a', color: '#f8fafc', boxShadow: '0 8px 30px rgba(0,0,0,0.18)' }}
                    itemStyle={{ color: '#f8fafc', fontSize: '12px' }}
                    labelStyle={{ fontWeight: 600, color: '#f8fafc', marginBottom: '4px' }}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }} />
                  <Area type="monotone" name="Estimated Cost" dataKey="estimatedCost" stroke="#8b5cf6" strokeDasharray="5 5" strokeWidth={2} fillOpacity={1} fill="url(#colorEst)" />
                  <Area type="monotone" name="Actual Cost" dataKey="outOfPocket" stroke="#3b82f6" strokeWidth={2.5} fillOpacity={1} fill="url(#colorSpend)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                <Activity className="w-12 h-12 mb-4 opacity-20" />
                <p className="text-xs sm:text-sm">No completed appointments or estimates to show trend.</p>
              </div>
            )}
          </div>
        </div>

        {/* Analytics Breakdown Row: Spending by Category & Provider-wise Spending */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
          {/* Spending by Category Card */}
          <div className="bg-card rounded-2xl sm:rounded-3xl border border-border shadow-xs p-4 sm:p-5 lg:p-6 flex flex-col">
            <div className="flex items-center justify-between pb-3 sm:pb-4 border-b border-border/60 mb-3 sm:mb-4">
              <h3 className="text-sm sm:text-base font-bold flex items-center gap-2 text-foreground">
                <PieChartIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary"/> Spending by Category
              </h3>
              {categoryData.length > 0 && (
                <span className="text-xs text-muted-foreground font-medium">
                  {categoryData.length} Categories
                </span>
              )}
            </div>
            <div className="w-full h-[320px] sm:h-[350px]">
              {categoryData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie 
                      data={categoryData} 
                      cx="50%" 
                      cy="38%" 
                      innerRadius={48} 
                      outerRadius={74} 
                      paddingAngle={3} 
                      dataKey="value"
                      stroke="var(--background)"
                      strokeWidth={2}
                    >
                      {categoryData.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                    </Pie>
                    <Tooltip 
                      formatter={(val: number) => {
                        const total = categoryData.reduce((sum: number, item: any) => sum + (Number(item.value) || 0), 0);
                        const pct = total > 0 ? Math.round(((Number(val) || 0) / total) * 100) : 0;
                        return [`$${Number(val).toLocaleString()} (${pct}%)`, "Spent"];
                      }}
                      contentStyle={{ 
                        borderRadius: '10px', 
                        border: '1px solid rgba(148, 163, 184, 0.25)', 
                        backgroundColor: '#0f172a', 
                        color: '#f8fafc', 
                        boxShadow: '0 8px 24px rgba(0,0,0,0.25)', 
                        padding: '6px 10px',
                        fontSize: '12px',
                      }}
                      itemStyle={{ color: '#38bdf8', fontSize: '11px', fontWeight: 600, padding: 0 }}
                      labelStyle={{ fontWeight: 600, color: '#f8fafc', marginBottom: '2px', fontSize: '11px' }}
                    />
                    <Legend 
                      iconType="circle" 
                      layout="horizontal"
                      align="center"
                      verticalAlign="bottom"
                      wrapperStyle={{ fontSize: '11px', paddingTop: '12px', lineHeight: '20px' }} 
                      formatter={(value: string) => (
                        <span className="text-[11px] text-foreground font-medium inline-block align-middle ml-1 mr-3">
                          {value}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                  <p className="text-xs sm:text-sm">No completed appointments to categorize.</p>
                </div>
              )}
            </div>
          </div>

          {/* Provider-wise Spending Card */}
          <div className="bg-card rounded-2xl sm:rounded-3xl border border-border shadow-xs p-4 sm:p-5 lg:p-6 flex flex-col">
            <div className="flex items-center justify-between pb-3 sm:pb-4 border-b border-border/60 mb-3 sm:mb-4">
              <h3 className="text-sm sm:text-base font-bold flex items-center gap-2 text-foreground">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-primary"/> Provider-wise Spending
              </h3>
              {providerCostData.length > 0 && (
                <span className="text-xs text-muted-foreground font-medium">
                  {providerCostData.length} Providers
                </span>
              )}
            </div>
            <div className="w-full h-[320px] sm:h-[350px]">
              {providerCostData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={providerCostData} 
                    layout="vertical" 
                    margin={{ top: 22, right: 25, left: 10, bottom: 12 }}
                    barCategoryGap="16%"
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} opacity={0.12} stroke="#94a3b8" />
                    <XAxis 
                      type="number" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 11, fill: '#64748b' }} 
                      tickFormatter={(v) => `$${v}`} 
                    />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 11, fill: '#64748b' }} 
                      width={130} 
                      tickFormatter={(val: string) => val.length > 18 ? `${val.slice(0, 16)}…` : val} 
                    />
                    <Tooltip 
                      cursor={false}
                      formatter={(val: number) => [`$${val.toLocaleString()}`, "Total Spent"]}
                      contentStyle={{ 
                        borderRadius: '10px', 
                        border: '1px solid rgba(148, 163, 184, 0.25)', 
                        backgroundColor: '#0f172a', 
                        color: '#f8fafc', 
                        boxShadow: '0 8px 24px rgba(0,0,0,0.25)', 
                        padding: '6px 10px',
                        fontSize: '12px',
                      }}
                      itemStyle={{ color: '#38bdf8', fontSize: '11px', fontWeight: 600, padding: 0 }}
                      labelStyle={{ fontWeight: 600, color: '#f8fafc', marginBottom: '2px', fontSize: '11px' }}
                    />
                    <Bar dataKey="cost" fill="#3b82f6" radius={[0, 6, 6, 0]} barSize={16}>
                      {providerCostData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground text-xs sm:text-sm text-center">
                  <p>No completed spending data to distribute.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Full-width Appointment History Table */}
        <div className="bg-card rounded-2xl sm:rounded-3xl border border-border shadow-xs overflow-hidden flex flex-col">
          <div className="px-5 sm:px-6 py-4 border-b border-border/60 flex justify-between items-center">
            <div>
              <h3 className="text-sm sm:text-base font-bold text-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-primary shrink-0"/> Appointment History
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 font-medium">Detailed breakdown of your past and upcoming healthcare visits.</p>
            </div>
          </div>

          {/* Mobile Card View (< md) */}
          <div className="block md:hidden divide-y divide-border/60">
            {recentAppointments.map((appt: any) => (
              <div key={appt.id} className="p-4 space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-muted-foreground">{appt.date}</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border font-bold text-[10px] ${getStatusColor(appt.status)}`}>
                    {appt.status}
                  </span>
                </div>
                <div>
                  <div className="font-bold text-sm text-foreground">{appt.provider}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {appt.type === 'Group' ? (
                      appt.assignedTo ? `Assigned: ${appt.assignedTo}` : 'Group Practice'
                    ) : 'Individual Practice'}
                  </div>
                </div>
                <div className="pt-2.5 mt-1 border-t border-border/50 flex items-center justify-between gap-3 text-xs">
                  <div className="min-w-0 flex-1">
                    <span className="text-xs text-muted-foreground font-medium block truncate">
                      {appt.service}
                    </span>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="font-extrabold text-primary text-sm tracking-tight">
                      ${appt.cost}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {recentAppointments.length === 0 && (
              <div className="px-6 py-8 text-center text-muted-foreground text-xs sm:text-sm">
                No appointments found.
              </div>
            )}
          </div>

          {/* Tablet & Desktop Table (>= md) */}
          <div className="hidden md:block overflow-x-auto w-full scrollbar-thin">
            <table className="w-full min-w-[640px] text-xs sm:text-sm text-left border-collapse">
              <thead className="bg-muted/50 text-muted-foreground font-bold uppercase text-[10px] sm:text-xs tracking-wider sticky top-0">
                <tr>
                  <th className="pl-5 sm:pl-6 pr-4 py-3 whitespace-nowrap w-[130px]">Date</th>
                  <th className="px-4 py-3 min-w-[180px]">Provider</th>
                  <th className="px-4 py-3 min-w-[200px]">Service</th>
                  <th className="px-4 py-3 text-right whitespace-nowrap w-[100px]">Cost</th>
                  <th className="pl-4 pr-5 sm:pr-6 py-3 text-center whitespace-nowrap w-[120px]">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {recentAppointments.map((appt: any) => (
                  <tr key={appt.id} className="hover:bg-muted/30 transition-colors">
                    <td className="pl-5 sm:pl-6 pr-4 py-3.5 font-medium text-foreground whitespace-nowrap">{appt.date}</td>
                    <td className="px-4 py-3.5">
                      <div className="font-bold text-foreground">{appt.provider}</div>
                      <div className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                         {appt.type === 'Group' ? (
                           appt.assignedTo ? `Assigned: ${appt.assignedTo}` : 'Group Practice'
                         ) : 'Individual Practice'}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-muted-foreground font-medium">{appt.service}</td>
                    <td className="px-4 py-3.5 text-right font-extrabold text-primary whitespace-nowrap">${appt.cost}</td>
                    <td className="pl-4 pr-5 sm:pr-6 py-3.5 text-center whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border font-bold text-[10px] sm:text-xs ${getStatusColor(appt.status)}`}>
                        {appt.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {recentAppointments.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground text-xs sm:text-sm">
                      No appointments found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 0 && (
            <div className="px-4 sm:px-6 py-3 border-t border-border/60 bg-muted/20">
              <ListPagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                totalCount={totalCount}
                limit={itemsPerPage}
                onLimitChange={(newLimit) => {
                  setItemsPerPage(newLimit);
                  setPage(1);
                }}
              />
            </div>
          )}
        </div>

      </div>
    </MemberLayout>
  );
}
